import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { get, post } from '@/features/shared/lib/api';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import MypageSideMenu from './MypageSideMenu';
import ResearcherCard from '@/features/researcher/components/ResearcherCard';
import PublicationCard from '@/features/publication/components/PublicationCard';
import ChangePassword from './edit/ChangePassword';
import ResearcherId from './edit/ResearcherId';
import SavedResearcher from './edit/SavedResearcher';
import Publications from './edit/Publications';
import Notification from './edit/Notification';
import Help from './edit/Help';
import Withdrawal from './edit/Withdrawal';

export default function MypageMyProfileEdit({ onCancelClick, record, loading, error, onUpdate }) {
  const location = useLocation();
  // const isPasswordPage = location.pathname === '/mypage/edit/password';
  const isResearcherIdPage = location.pathname === '/mypage/edit/researcher-id';
  const isSavedResearcherPage = location.pathname === '/mypage/edit/saved-researcher';
  const isPublicationsPage = location.pathname === '/mypage/edit/publications';
  const isNotificationPage = location.pathname === '/mypage/edit/settings/notification';
  const isWithdrawalPage = location.pathname === '/mypage/edit/settings/withdrawal';
  const isHelpPage = location.pathname === '/mypage/edit/support/help';
  const isNoticePage = location.pathname === '/mypage/edit/support/notice';
  // const isFaqPage = location.pathname === '/mypage/edit/support/faq';
  // record에서 데이터 초기화

  const getInitialFormData = useMemo(() => {
    // privacy_settings 없으면 {} 로 두고, 각 키 없으면 false
    const getPrivacy = (rec, key) => {
      const ps = rec?.privacy_settings;
      if (!ps || typeof ps !== 'object') return false;
      const v = ps[key];
      return v === true || v === 1;
    };

  if (!record) {
      return {
        scholar_name_eng: '',
        scholar_name_kor: '',
        email: '',
        researchArea: '',
        website: '',
        telephone: '',
        labName: '',
        labLink: '',
        introduction: '',
        education: '',
        career: '',
        is_email: false,
        is_research_field: false,
        is_website: false,
        is_telephone: false,
        is_lab_name: false,
        is_lab_link: false,
        is_intro: false,
        is_education_history: false,
        is_career: false,
      };
    }
    // research_field 변환: 세미콜론(;)을 줄바꿈(\n)으로 변환
    let researchArea = '';
    if (record.research_field) {
      if (Array.isArray(record.research_field)) {
        researchArea = record.research_field.map(k => k?.keyword).filter(Boolean).join('\n');
      } else if (typeof record.research_field === 'string') {
        // 문자열인 경우 세미콜론을 줄바꿈으로 변환
        researchArea = record.research_field.split(';').map(item => item.trim()).filter(Boolean).join('\n');
      }
    }

    return {
      scholar_name_eng: record.scholar_name_eng || '',
      scholar_name_kor: record.scholar_name_kor || '',
      email: record.email || '',
      researchArea: researchArea,
      website: record.homepage || '',
      telephone: record.telephone || '',
      labName: record.lab_name || '',
      labLink: record.lab_link || '',
      introduction: record.introduction || '',
      education: record.education || '',
      career: record.career || '',
      is_email: getPrivacy(record, 'is_email'),
      is_research_field: getPrivacy(record, 'is_research_field'),
      is_website: getPrivacy(record, 'is_website'),
      is_telephone: getPrivacy(record, 'is_telephone'),
      is_lab_name: getPrivacy(record, 'is_lab_name'),
      is_lab_link: getPrivacy(record, 'is_lab_link'),
      is_intro: getPrivacy(record, 'is_intro'),
      is_education_history: getPrivacy(record, 'is_education_history'),
      is_career: getPrivacy(record, 'is_career'),
    };
  }, [record]);

  const [formData, setFormData] = useState(getInitialFormData);
  const [editingField, setEditingField] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(record?.profile_pic || rmVars.themeUrl + 'images/no-profile-gy.png');
  // additionalImages: [{ url: string (base64 또는 URL), file: File | null, order: number }]
  const [additionalImages, setAdditionalImages] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null); // 업로드할 파일 저장
  const [isPhotoDeleted, setIsPhotoDeleted] = useState(false); // 삭제 플래그
  const [isUpdating, setIsUpdating] = useState(false); // 업데이트 중 플래그

  // record가 변경되면 formData 업데이트
  useEffect(() => {
    if (record) {
      setFormData(getInitialFormData);
      if (record.profile_pic) {
        setProfilePhoto(record.profile_pic);
      } else {
        setProfilePhoto(rmVars.themeUrl + 'images/no-profile-gy.png');
      }
      // record가 변경되면 삭제 플래그와 선택된 파일 초기화
      setIsPhotoDeleted(false);
      setSelectedPhotoFile(null);
      // 기존 additional images 초기화 (record에서 가져오기)
      if (record.images && Array.isArray(record.images)) {
        const homeUrl = typeof rmVars !== 'undefined' && rmVars.homeUrl ? rmVars.homeUrl : (typeof window !== 'undefined' ? window.location.origin : '');
        const images = record.images.map((url, index) => ({
          url: url.startsWith('http') ? url : (homeUrl + url.replace(/^\/+/, '/')),
          file: null,
          order: index
        }));
        setAdditionalImages(images);
      } else {
        setAdditionalImages([]);
      }
    }
  }, [record, getInitialFormData]);

  const handleFieldClick = (field) => {
    setEditingField(editingField === field ? null : field);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      e.target.value = ''; // 파일 선택 초기화
      return;
    }

    // 파일 타입 체크
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('JPG, PNG, GIF 파일만 업로드 가능합니다.');
      e.target.value = ''; // 파일 선택 초기화
      return;
    }

    // 파일을 state에 저장 (나중에 업로드용)
    setSelectedPhotoFile(file);
    setIsPhotoDeleted(false); // 새 파일 선택 시 삭제 플래그 해제

    // 미리보기용으로 FileReader 사용
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // 이미지 업로드 API 호출 함수
  const uploadPhoto = async () => {
    if (!selectedPhotoFile) return null;

    try {
      setUploadingPhoto(true);

      // FormData 생성
      const formData = new FormData();
      formData.append('file', selectedPhotoFile);

      // API 호출
      const baseUrl = typeof rmVars !== 'undefined' ? rmVars.restApi : '/scholar/wp-json';
      const finalUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/photo';

      const response = await axios.post(finalUrl, formData, {
        headers: {
          'X-WP-Nonce': typeof rmVars !== 'undefined' ? rmVars.api_nonce : '',
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.status) {
        // 업로드 성공 - 응답에서 이미지 URL 가져오기
        const uploadedImageUrl = response.data.result?.profile_pic || response.data.result?.url || response.data.result;
        if (uploadedImageUrl) {
          setProfilePhoto(uploadedImageUrl);
          setSelectedPhotoFile(null); // 업로드 완료 후 파일 초기화
          setIsPhotoDeleted(false); // 업로드 성공 시 삭제 플래그 해제
          return uploadedImageUrl;
        }
      } else {
        throw new Error(response.data?.message || '이미지 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      throw new Error(error.response?.data?.message || error.message || '이미지 업로드에 실패했습니다.');
    } finally {
      setUploadingPhoto(false);
    }
    return null;
  };

  // 이미지 삭제 API 호출 함수
  const deletePhoto = async () => {
    if (!isPhotoDeleted) return;

    try {
      setUploadingPhoto(true);

      // 삭제 API 호출
      const baseUrl = typeof rmVars !== 'undefined' ? rmVars.restApi : '/scholar/wp-json';
      const finalUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/photo';

      const response = await axios.post(finalUrl, null, {
        params: { delete: 'true' },
        headers: {
          'X-WP-Nonce': typeof rmVars !== 'undefined' ? rmVars.api_nonce : '',
        },
      });

      if (response.data && response.data.status) {
        // 삭제 성공
        setIsPhotoDeleted(false); // 삭제 완료 후 플래그 초기화
        setSelectedPhotoFile(null); // 선택된 파일도 초기화
      } else {
        throw new Error(response.data?.message || '프로필 사진 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('프로필 사진 삭제 오류:', error);
      throw new Error(error.response?.data?.message || error.message || '프로필 사진 삭제에 실패했습니다.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAdditionalImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // 파일 크기 및 타입 검증
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name}: 파일 크기는 5MB 이하여야 합니다.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name}: JPG, PNG, GIF 파일만 업로드 가능합니다.`);
        return false;
      }
      return true;
    });

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdditionalImages(prev => {
          const maxOrder = prev.length > 0 ? Math.max(...prev.map(img => img.order)) : -1;
          return [...prev, {
            url: reader.result,
            file: file,
            order: maxOrder + 1
          }];
        });
      };
      reader.readAsDataURL(file);
    });

    // 파일 입력 초기화
    e.target.value = '';
  };

  const handleDeletePhoto = () => {
    // 로컬 상태만 변경 (API 호출은 Update 버튼에서)
    setProfilePhoto(rmVars.themeUrl + 'images/no-profile-gy.png');
    setSelectedPhotoFile(null); // 선택된 파일 초기화
    setIsPhotoDeleted(true); // 삭제 플래그 설정
  };

  const handleDeleteImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index).map((img, idx) => ({ ...img, order: idx })));
  };

  // 이미지 순서 변경 (위로 이동)
  const handleMoveImageUp = (index) => {
    if (index === 0) return;
    setAdditionalImages(prev => {
      const newImages = [...prev];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      return newImages.map((img, idx) => ({ ...img, order: idx }));
    });
  };

  // 이미지 순서 변경 (아래로 이동)
  const handleMoveImageDown = (index) => {
    setAdditionalImages(prev => {
      if (index === prev.length - 1) return prev;
      const newImages = [...prev];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      return newImages.map((img, idx) => ({ ...img, order: idx }));
    });
  };

  const renderEditableField = (field, label, value, isTextarea = false, rows = 1, visibilityKey = null) => {
    const isEditing = editingField === field;
    // visibilityKey가 없으면 자동으로 is_ + field로 생성
    const visibilityField = visibilityKey || `is_${field}`;
    const isVisible = formData[visibilityField] !== false;

    return (
      <div className="w-full">
        <div className="edit-field flex flex-col lg:flex-row gap-[10px] lg:items-center w-full" data-field={field}>
          <span className="lg:w-[160px] text-[14px] font-medium lg:text-[#6C7687]">{label}</span>
          <div className="w-full lg:w-[calc(100%-160px)]">
            {!isEditing ? (
              <p
                className={`display-text w-full ${isTextarea ? 'h-[120px] lg:h-[186px] px-4 py-[14px]' : 'h-[44px] px-4 leading-[42px]'} border border-[#E4E8F1] rounded-[8px] text-[13px] cursor-pointer ${isTextarea ? 'whitespace-pre-wrap overflow-y-auto' : ''}`}
                onClick={() => handleFieldClick(field)}
                data-value={value}
              >
                {value || '입력하세요'}
              </p>
            ) : (
              isTextarea ? (
                <textarea
                  className="edit-input w-full h-[120px] lg:h-[186px] px-4 py-[14px] border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none"
                  value={value}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  onBlur={() => setEditingField(null)}
                  autoFocus
                />
              ) : (
                <input
                  type="text"
                  className="edit-input w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none"
                  value={value}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  onBlur={() => setEditingField(null)}
                  autoFocus
                />
              )
            )}
          </div>
        </div>
        <div className="flex items-center gap-[6px] mt-3 lg:pl-[170px] text-[13px] text-[#6C7687] font-medium">
          <span>프로필 노출</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isVisible}
              onChange={(e) => handleInputChange(visibilityField, e.target.checked)}
            />
            <div className="w-[25px] h-[15px] bg-[#B4BCCC] rounded-full peer-checked:bg-[#4B4DFF] transition-colors duration-200"></div>
            <div className="absolute right-[2px] top-[2px] w-[11px] h-[11px] bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-[-10px]"></div>
          </label>
          <span>비노출</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mypage-edit-page flexPage flex-col lg:flex-row mt-[127px] lg:mt-[120px] lg:px-6 2xl:px-[100px]">
      {/* 마이페이지는 margin-top값이 다름 */}
      <div className="mypage-edit-left w-full lg:w-[310px] mt-[30px] lg:mt-[68px] px-6 lg:px-0">
        <PageBreadcrumb
          parent="My page"
          parentTo="/mypage"
          current={
            // isPasswordPage
            // ? 'Change Password'
            isResearcherIdPage
              ? 'Researcher ID'
              : isSavedResearcherPage
                ? 'Saved Researcher'
                : isPublicationsPage
                  ? 'Publications'
                  : isNotificationPage
                    ? 'Notification'
                    : isWithdrawalPage
                      ? '회원 탈퇴'
                      : isHelpPage
                      ? 'Help & Support'
                        : 'My profile'
          }
        />

        <div className="tit-area w-full flex justify-between gap-3">
          <h2 className="text-[26px] leading-[130%] font-black lg:text-[32px]">My page</h2>
        </div>

        {/* mypage menu */}
        <div className="hidden lg:block bg-white rounded-[12px] mt-8 px-6 py-10 lg:pb-6">
          {/* 회원 프로필 */}
          <div>
            <div className="w-[96px] h-[96px] mx-auto mb-4 rounded-full overflow-hidden border border-[#E4E8F1]">
              <img src={record?.profile_pic || rmVars.themeUrl + 'images/no-profile-gy.png'} className="w-full h-full object-cover object-center" alt="photo" />
            </div>

            <div className="mb-[10px] text-[16px] leading-[140%] font-bold text-center">
              {loading ? '로딩 중...' : (record ? [record.scholar_name_eng || record.scholar_name, record.scholar_name_kor].filter(Boolean).join(' / ') : '이름 없음')}
            </div>

            <div className="flex flex-col gap-1 text-[12px] text-[#8D9AAF] text-center leading-[140%]">
              <p>{record?.dept_name || record?.col_name || ''}</p>
              <p>{record?.sinbun_name || ''}</p>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={onCancelClick}
                className="w-[calc((100%-8px)/2)] h-[42px] text-[14px] text-[#828293] font-semibold text-center leading-[40px] border border-[#E4E8F1] rounded-[12px] cursor-pointer"
              >
                View profile
              </button>
              <Link to="/mypage/edit/profile" className="w-[calc((100%-8px)/2)] h-[42px] text-[14px] text-[#828293] font-semibold text-center leading-[40px] border border-[#E4E8F1] rounded-[12px]">
                Edit profile
              </Link>
            </div>
          </div>

          <MypageSideMenu />
        </div>
      </div>

      <div className="mypage-edit-content w-full px-6 lg:px-[0] pt-[30px] lg:pt-[0] lg:w-[calc(100%-360px)] lg:mt-[174px]">
        {isResearcherIdPage ? (
          <ResearcherId record={record} />
        ) : isSavedResearcherPage ? (
          <SavedResearcher />
        ) : isPublicationsPage ? (
          <Publications />
        ) : isNotificationPage ? (
          <Notification />
        ) : isWithdrawalPage ? (
          <Withdrawal />
        ) : isHelpPage ? (
          <Help />
        ) : isNoticePage ? (
          <Notice />
        ) : (
          <div>
            <div className="hidden lg:flex justify-between items-center w-full">
              <h3 className="text-[24px] font-bold leading-[100%]">My Profile</h3>

              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  onClick={async () => {
                    if (isUpdating) return;
                    try {
                      setIsUpdating(true);

                      // 1. 프로필 사진 삭제 먼저 실행
                      if (isPhotoDeleted) {
                        await deletePhoto();
                      }

                      // 2. 프로필 사진 업로드 실행
                      if (selectedPhotoFile) {
                        await uploadPhoto();
                      }

                      // 3. 폼 데이터 준비
                      // research_field 변환: 줄바꿈(\n)을 세미콜론(;)으로 변환하여 문자열로 전달
                      const researchFieldValue = formData.researchArea
                        ? formData.researchArea.split('\n').map(item => item.trim()).filter(Boolean).join(';')
                        : '';

                      const privacy_settings = {
                        is_email: formData.is_email === true,
                        is_research_field: formData.is_research_field === true,
                        is_website: formData.is_website === true,
                        is_telephone: formData.is_telephone === true,
                        is_lab_name: formData.is_lab_name === true,
                        is_lab_link: formData.is_lab_link === true,
                        is_intro: formData.is_intro === true,
                        is_education_history: formData.is_education_history === true,
                        is_career: formData.is_career === true,
                      };

                      const updateData = {
                        scholar_name_eng: formData.scholar_name_eng || '',
                        scholar_name_kor: formData.scholar_name_kor || '',
                        email: formData.email || '',
                        research_field: researchFieldValue,
                        website: formData.website || '',
                        telephone: formData.telephone || '',
                        lab_name: formData.labName || '',
                        lab_link: formData.labLink || '',
                        introduction: formData.introduction || '',
                        education: formData.education || '',
                        career: formData.career || '',
                        privacy_settings,
                      };

                      // 4. Additional Images 처리: FormData로 전송
                      const formDataToSend = new FormData();

                      // 텍스트 데이터 추가
                      Object.keys(updateData).forEach(key => {
                        if (key === 'privacy_settings') {
                          formDataToSend.append(key, JSON.stringify(updateData[key]));
                        } else {
                          formDataToSend.append(key, updateData[key]);
                        }
                      });

                      // Additional Images 추가
                      // 새 파일과 기존 URL을 분리해서 전송
                      const newFiles = [];
                      const existingUrls = [];
                      additionalImages.forEach((img, index) => {
                        if (img.file) {
                          // 새 파일은 별도 배열로 추가
                          newFiles.push({
                            file: img.file,
                            order: img.order
                          });
                          formDataToSend.append(`additional_images_file_${index}`, img.file);
                          formDataToSend.append(`additional_images_order_${index}`, img.order);
                          formDataToSend.append(`additional_images_type_${index}`, 'file');
                        } else {
                          // 기존 URL은 URL만 전송 (home_url 제외)
                          const homeUrl = typeof rmVars !== 'undefined' && rmVars.homeUrl ? rmVars.homeUrl : (typeof window !== 'undefined' ? window.location.origin : '');
                          const urlWithoutHome = img.url.replace(homeUrl, '').replace(/^\/+/, '/');
                          existingUrls.push({
                            url: urlWithoutHome,
                            order: img.order
                          });
                          formDataToSend.append(`additional_images_url_${index}`, urlWithoutHome);
                          formDataToSend.append(`additional_images_order_${index}`, img.order);
                          formDataToSend.append(`additional_images_type_${index}`, 'url');
                        }
                      });

                      // 총 이미지 개수 전송
                      formDataToSend.append('additional_images_count', additionalImages.length);

                      // 5. API 호출
                      const baseUrl = typeof rmVars !== 'undefined' ? rmVars.restApi : '/scholar/wp-json';
                      const finalUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/info';

                      const response = await axios.post(finalUrl, formDataToSend, {
                        headers: {
                          'X-WP-Nonce': typeof rmVars !== 'undefined' ? rmVars.api_nonce : '',
                          'Content-Type': 'multipart/form-data',
                        },
                      });

                      if (response.data && response.data.status) {
                        // 데이터 다시 불러오기
                        if (onUpdate) {
                          await onUpdate();
                        }
                        // 업데이트 완료 후 View 모드로 전환
                        onCancelClick();
                      } else {
                        throw new Error(response.data?.result?.message || '업데이트에 실패했습니다.');
                      }
                    } catch (error) {
                      console.error('Update error:', error);
                      alert(error.response?.data?.result?.message || error.message || '업데이트에 실패했습니다.');
                    } finally {
                      setIsUpdating(false);
                    }
                  }}
                  disabled={uploadingPhoto || isUpdating}
                  className={`btn-edit flex rounded-[12px] items-center justify-center gap-2 px-4 py-3 text-[14px] text-white font-semibold leading-[100%] bg-[#333] ${(uploadingPhoto || isUpdating) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <img src={rmVars.themeUrl + 'images/check-ico.png'} className="w-[18px]" alt="update" />
                  <span>{(uploadingPhoto || isUpdating) ? '업데이트 중...' : 'Update'}</span>
                </button>
              </div>
            </div>

            <div className="mypage-card lg:mt-[18px] lg:p-[60px] bg-white rounded-[12px]">
              {/* Edit Photo */}
              <div className="pb-[36px] lg:pb-[50px] mb-[36px] lg:mb-[50px] border-b border-b-[#E4E8F1]">
                <h4 className="mb-8 text-[18px] font-bold leading-[100%]">Edit Photo</h4>

                <div className="flex items-center gap-4 relative">
                  <div className="photo-preview-area w-[96px] h-[96px] border border-[#E4E8F1] rounded-full overflow-hidden flex-shrink-0">
                    <img src={profilePhoto} alt="프로필 사진 미리보기" className="profile-photo w-full h-full object-cover object-center block" />
                  </div>

                  {profilePhoto !== rmVars.themeUrl + 'images/no-profile-gy.png' && (
                    <button
                      type="button"
                      onClick={handleDeletePhoto}
                      className="delete-photo-btn absolute top-0 left-[70px] w-[26px] h-[26px] rounded-full bg-[#333] text-white text-[12px] font-bold flex items-center justify-center cursor-pointer border border-[#fff]"
                    >
                      X
                    </button>
                  )}

                  <div>
                    <div className="upload-controls">
                      <input
                        type="file"
                        id="file-upload"
                        accept=".jpg, .jpeg, .png, .gif"
                        className="hidden"
                        onChange={handlePhotoUpload}
                      />
                      <label
                        htmlFor="file-upload"
                        className="upload-btn h-[42px] px-[28px] text-[14px] font-semibold text-[#828293] bg-white rounded-[12px] border border-[#E4E8F1] focus:outline-none cursor-pointer inline-flex items-center justify-center"
                      >
                        File upload
                      </label>
                    </div>

                    <p className="upload-info mt-[10px] text-[12px] text-[#8D9AAF] leading-[140%]">
                      5MB 이하의 JPG, GIF, PNG 파일을 지원합니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Edit Profile */}
              <div className="pb-[36px] lg:pb-[50px] mb-[36px] lg:mb-[50px] border-b border-b-[#E4E8F1]">
                <h4 className="mb-8 text-[18px] font-bold leading-[100%]">Edit Profile</h4>

                <div className="flex flex-col gap-[30px] lg:gap-8">
                  {/* 영어이름 */}
                  <div className="flex flex-col lg:flex-row gap-[10px] lg:items-center w-full">
                    <span className="lg:w-[160px] text-[14px] font-medium lg:text-[#6C7687]">English Name (영문명)</span>
                    <div className="lg:w-[calc(100%-160px)]">
                      <input
                        type="text"
                        placeholder="Kim, Taewoo"
                        value={formData.scholar_name_eng || ''}
                        onChange={(e) => handleInputChange('scholar_name_eng', e.target.value)}
                        className="w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none bg-white"
                      />
                    </div>
                  </div>

                  {/* 한글이름 */}
                  <div className="flex flex-col lg:flex-row gap-[10px] lg:items-center w-full">
                    <span className="lg:w-[160px] text-[14px] font-medium lg:text-[#6C7687]">Korean Name (한글명)</span>
                    <div className="lg:w-[calc(100%-160px)]">
                      <input
                        type="text"
                        placeholder="김태우"
                        value={formData.scholar_name_kor || ''}
                        onChange={(e) => handleInputChange('scholar_name_kor', e.target.value)}
                        className="w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none bg-white"
                      />
                    </div>
                  </div>

                  {/* 이메일 */}
                  {renderEditableField('email', 'E-mail', formData.email, false, 1, 'is_email')}

                  {/* Research Area */}
                  {renderEditableField('researchArea', 'Research Area', formData.researchArea, true, 1, 'is_research_field')}

                  {/* Website */}
                  {renderEditableField('website', 'Website', formData.website, false, 1, 'is_website')}

                  {/* Telephone */}
                  {renderEditableField('telephone', 'Telephone', formData.telephone, false, 1, 'is_telephone')}

                  {/* Lab Name */}
                  {renderEditableField('labName', 'Lab Name', formData.labName, false, 1, 'is_lab_name')}

                  {/* Lab Link */}
                  {renderEditableField('labLink', 'Lab Link', formData.labLink, false, 1, 'is_lab_link')}
                </div>
              </div>

              {/* Introduction */}
              <div>
                <h4 className="mb-8 text-[18px] font-bold leading-[100%]">Introduction</h4>

                <div className="flex flex-col gap-[30px] lg:gap-6">
                  {/* Introduction */}
                  {renderEditableField('introduction', 'Introduction', formData.introduction, true, 1, 'is_intro')}

                  {/* Educational History */}
                  {renderEditableField('education', 'Educational History', formData.education, true, 1, 'is_education_history')}

                  {/* Career */}
                  {renderEditableField('career', 'Career', formData.career, true, 1, 'is_career')}

                  {/* Additional Images */}
                  <div className="w-full">
                    <div className="edit-field flex flex-col gap-[10px] w-full" data-field="additionalImages">
                      <span className="text-[14px] font-medium lg:text-[#6C7687]">Additional Images</span>

                      <div className="image-uploader-container">
                        <div className="flex flex-wrap gap-3">
                          {additionalImages.map((img, idx) => (
                            <div key={idx} className="relative w-[calc((100%-12px)/2)] md:w-[calc((100%-24px)/3)] xl:w-[calc((100%-36px)/4)] 2xl:w-[calc((100%-48px)/5)] aspect-square border border-[#E4E8F1] rounded-[12px] overflow-hidden group">
                              <img src={typeof img === 'string' ? img : img.url} alt={`추가 이미지 ${idx + 1}`} className="w-full h-full object-cover" />
                              {/* 순서 조절 버튼 */}
                              <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  type="button"
                                  onClick={() => handleMoveImageUp(idx)}
                                  disabled={idx === 0}
                                  className={`w-6 h-6 bg-[#333] text-white text-[12px] font-bold rounded flex items-center justify-center ${idx === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  title="위로 이동"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleMoveImageDown(idx)}
                                  disabled={idx === additionalImages.length - 1}
                                  className={`w-6 h-6 bg-[#333] text-white text-[12px] font-bold rounded flex items-center justify-center ${idx === additionalImages.length - 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  title="아래로 이동"
                                >
                                  ↓
                                </button>
                              </div>
                              {/* 삭제 버튼 */}
                              <button
                                type="button"
                                onClick={() => handleDeleteImage(idx)}
                                className="absolute top-2 right-2 w-6 h-6 bg-[#333] text-white text-[12px] font-bold rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="삭제"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <label className="relative w-[calc((100%-12px)/2)] md:w-[calc((100%-24px)/3)] xl:w-[calc((100%-36px)/4)] 2xl:w-[calc((100%-48px)/5)] aspect-square flex items-center justify-center border border-[#E4E8F1] rounded-[12px] cursor-pointer hover:border-[#8D9AAF] transition-colors">
                            <span className="w-[44px] h-[44px] text-[#6C7687] text-[36px] leading-[40px] text-center font-light rounded-full bg-[#F3F5F9]">+</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              onChange={handleAdditionalImagesUpload}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
